import { differenceInMinutes, isAfter, subDays } from "date-fns";
import { FC, useMemo, useState } from "react";

import { AttendanceCard } from "./AttendanceCard";
import { EngagementCard } from "./EngagementCard";
import { EngagementTable } from "./EngagementTable";
import { SessionLogTable } from "./SessionLogTable";
import { SessionsCard } from "./SessionsCard";
import { StudentTalkTimeCard } from "./StudentTalkTimeCard";

import {
  ClassSessionSummaryType,
  EngagementLevel,
  UserEngagementDataType,
} from "api/api-data-types";
import { getClassSessionSummaries, serverGetEngagementData } from "api/api-server-data";
import { ErrorScreen, LoadingScreen } from "components/LoadingScreen";
import { ALL_TIME_DATA } from "components/constants";
import { t } from "i18n/i18n";
import { SortableTableDateRange, SortableTableDateRangeOption } from "mds/components/SortableTable";
import { useCapabilities, useUserIdsWhoCanParticipateInCourse } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectAllVisibleClassSessionIdsForCurrentCourse,
  selectCurrentCourseId,
} from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

const MIN_DURATION_CLASS_MINUTES = 5;

const isVirtualClassSession = (classSession: ClassSessionSummaryType) => {
  return classSession.view === "virtual-class";
};

export const SessionLogView: FC = () => {
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const { canAuthorCourse } = useCapabilities();
  const [selectedDateRange, setSelectedDateRange] = useState<SortableTableDateRangeOption | null>(
    null,
  );
  const visibleClassSessionIds = useAppSelector(selectAllVisibleClassSessionIdsForCurrentCourse);

  const {
    data: classSessions,
    isLoading: isLoadingClassSessions,
    error: errorClassSessions,
  } = usePromiseEffect<ClassSessionSummaryType[]>(async () => {
    if (!currentCourseId) {
      return [];
    }
    return getClassSessionSummaries(currentCourseId);
  }, [currentCourseId]);

  const {
    data: engagementData,
    isLoading: isLoadingEngagementData,
    error: errorEngagementData,
  } = usePromiseEffect<Record<number, Record<string, UserEngagementDataType>> | null>(async () => {
    if (!currentCourseId) {
      return null;
    }
    return serverGetEngagementData(currentCourseId);
  }, [currentCourseId]);

  const allValidClassSessions = useMemo(() => {
    if (!classSessions) return [];
    return classSessions.filter((cs) => {
      if (!visibleClassSessionIds.includes(cs.class_session_id)) return false;
      if (!cs.start_time || !cs.end_time) return false;
      const duration = differenceInMinutes(cs.end_time, cs.start_time);
      if (duration < MIN_DURATION_CLASS_MINUTES) return false;
      return true;
    });
  }, [classSessions, visibleClassSessionIds]);

  // Filter sessions by date range
  const filteredClassSessions = useMemo(() => {
    if (!selectedDateRange || selectedDateRange.value === ALL_TIME_DATA) {
      return allValidClassSessions;
    }
    const filterDate = subDays(new Date(), selectedDateRange.value);
    return allValidClassSessions.filter((cs) => {
      if (!cs.start_time) return false;
      return isAfter(new Date(cs.start_time), filterDate);
    });
  }, [allValidClassSessions, selectedDateRange]);

  // Filter engagement data by selected date range.
  const filteredEngagementData = useMemo(() => {
    if (!engagementData) return null;
    const selectedRange = selectedDateRange?.value ?? ALL_TIME_DATA;
    return engagementData[selectedRange] ?? null;
  }, [engagementData, selectedDateRange]);

  // Filter out non-student users from engagement data.
  const studentUserIds = useUserIdsWhoCanParticipateInCourse();
  const studentEngagementData = useMemo(() => {
    if (!filteredEngagementData) return null;
    return Object.fromEntries(
      Object.entries(filteredEngagementData).filter(([userId]) => studentUserIds.includes(userId)),
    );
  }, [filteredEngagementData, studentUserIds]);

  // To avoid cards disappearing when the date range is changed, let's use all class sessions to compute hasVirtualSessions and hasEngagementData.
  const hasVirtualSessions = useMemo(() => {
    return allValidClassSessions.some((session) => isVirtualClassSession(session));
  }, [allValidClassSessions]);

  const hasEngagementData = useMemo(() => {
    if (!engagementData) return false;
    const allTimeEngagementData = engagementData[ALL_TIME_DATA] ?? {};
    const userEntries = Object.entries(allTimeEngagementData);
    if (userEntries.length === 0) return false;
    return userEntries.some(
      ([userId, data]) =>
        studentUserIds.includes(userId) &&
        (data.effort_level !== EngagementLevel.NONE ||
          data.performance_level !== EngagementLevel.NONE),
    );
  }, [engagementData, studentUserIds]);

  if (isLoadingClassSessions || isLoadingEngagementData) {
    return <LoadingScreen>{t("common.loading")}...</LoadingScreen>;
  }
  if (errorClassSessions || errorEngagementData) {
    return <ErrorScreen>Error loading session logs</ErrorScreen>;
  }

  return (
    <main className="mt-4">
      <div className="mb-8 flex w-full items-center justify-between rounded-lg bg-black-tint-97 px-4 py-2">
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-black-tint-20">Showing data from:</p>
          <SortableTableDateRange value={selectedDateRange} onChange={setSelectedDateRange} />
        </div>
        <p className="text-sm font-medium text-black-tint-55">
          {t("session_log_view.applies_to_all_metrics")}
        </p>
      </div>
      <div className="flex flex-col gap-10">
        {canAuthorCourse && (
          <div className="flex w-full gap-4">
            <div className="flex-1">
              <SessionsCard sessions={filteredClassSessions} />
            </div>
            <div className="flex-1">
              <AttendanceCard sessions={filteredClassSessions} />
            </div>
            {hasEngagementData && (
              <div className="flex-1">
                <EngagementCard engagementData={studentEngagementData} />
              </div>
            )}
            {hasVirtualSessions && (
              <div className="flex-1">
                <StudentTalkTimeCard sessions={filteredClassSessions} />
              </div>
            )}
          </div>
        )}
        <SessionLogTable
          allValidClassSessions={allValidClassSessions}
          classSessions={filteredClassSessions}
        />
        {canAuthorCourse && (
          <EngagementTable
            classSessions={filteredClassSessions}
            engagementData={studentEngagementData}
          />
        )}
      </div>
    </main>
  );
};
